/**
 * XML Validate Tool
 *
 * Validates XML syntax and reports errors with details.
 */

import { XMLParser, XMLValidator } from 'fast-xml-parser';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const XmlValidateTool: ITransformerTool = {
  id: 'xml-validate',
  name: 'XML Validate',
  description: 'Validate XML syntax and report any errors',
  version: '1.0.0',
  type: 'transformer',
  category: 'Validators & Testers',
  keywords: ['xml', 'validate', 'check', 'verify', 'lint', 'syntax'],

  seo: {
    slug: 'xml-validator',
    pageTitle: 'XML Validator - Free Online Syntax Checker | SWE DevTools',
    metaDescription: 'Validate XML syntax and see detailed error messages with line and column numbers. Check well-formedness instantly. Fast, private, runs in your browser.',
    keywords: ['xml validator', 'xml syntax checker', 'validate xml online', 'xml lint', 'xml checker'],
    relatedTools: ['xml-to-json', 'xml-to-yaml', 'json-validate'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.XML, SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'allowBooleanAttributes',
      type: 'boolean',
      label: 'Allow Boolean Attributes',
      defaultValue: true,
      description: 'Allow attributes without values (e.g., <input disabled>)',
    },
  ],

  supportsInput(_input: string, detectedType?: string): boolean {
    return detectedType === 'XML' || detectedType === 'Text';
  },

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const allowBooleanAttributes = config.allowBooleanAttributes !== false;
    const text = String(input ?? '').trim();

    const validationResult = XMLValidator.validate(text, {
      allowBooleanAttributes,
    });

    if (validationResult === true) {
      // Parse to get structure info
      try {
        const parser = new XMLParser({
          ignoreAttributes: false,
          allowBooleanAttributes,
        });
        const parsed = parser.parse(text);
        const rootKeys = Object.keys(parsed).filter((k) => k !== '?xml');
        const rootElement = rootKeys.length > 0 ? rootKeys[0] : '(none)';

        return `✓ Valid XML\nRoot Element: <${rootElement}>`;
      } catch {
        return '✓ Valid XML';
      }
    }

    const err = validationResult as { err: { code: string; msg: string; line: number; col: number } };
    const { msg, line, col } = err.err;
    return `✗ Invalid XML\nError: ${msg}\nLine: ${line}, Column: ${col}`;
  },
};
